import { useCallback, useEffect, useState, useRef } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useAuth } from '@clerk/nextjs';
import { UserRole } from '@/lib/roles-client';

export type User = {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string;
  createdAt: string;
  fullName: string;
};

// Constants
const USER_STORAGE_KEY = 'droquimar-user-data';
const FETCH_COOLDOWN_MS = 60000; // 1 minute

export function useUser() {
  const router = useRouter();
  const pathname = usePathname();
  const { isLoaded, isSignedIn, userId } = useAuth();
  
  // Refs to prevent unnecessary rerenders
  const fetchInProgress = useRef(false);
  const lastFetchTimeRef = useRef<number | null>(null);
  const refreshInProgress = useRef(false);
  
  // State management
  const [user, setUser] = useState<User | null>(() => {
    // Initialize from localStorage if available
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem(USER_STORAGE_KEY);
        return stored ? JSON.parse(stored) : null;
      } catch (e) {
        return null;
      }
    }
    return null;
  });
  const [storedUser, setStoredUser] = useState<User | null>(() => {
    // Initialize from localStorage if available
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem(USER_STORAGE_KEY);
        return stored ? JSON.parse(stored) : null;
      } catch (e) {
        return null;
      }
    }
    return null;
  });
  const [isLoading, setIsLoading] = useState<boolean>(!user && !storedUser);
  const [error, setError] = useState<Error | null>(null);

  // Store user in localStorage
  const updateStoredUser = useCallback((userData: User | null) => {
    if (typeof window !== 'undefined') {
      try {
        if (userData) {
          localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userData));
        } else {
          localStorage.removeItem(USER_STORAGE_KEY);
        }
        setStoredUser(userData);
      } catch (e) {
        // Silent error
      }
    }
  }, []);

  // Determine if we should fetch user data
  const shouldFetch = useCallback(() => {
    // Don't fetch if already fetching
    if (fetchInProgress.current) {
      return false;
    }

    // If user is not authenticated according to Clerk, don't fetch
    if (isLoaded && !isSignedIn) {
      return false;
    }

    // If Clerk auth is still loading, don't fetch yet
    if (!isLoaded) {
      return false;
    }

    // Check cooldown period - don't fetch if we fetched recently
    if (lastFetchTimeRef.current && Date.now() - lastFetchTimeRef.current < FETCH_COOLDOWN_MS) {
      return false;
    }

    return true;
  }, [isLoaded, isSignedIn]);

  // Fetch user data
  const fetchUserData = useCallback(async (force: boolean = false) => {
    if (!force && !shouldFetch()) return;

    // Prevent concurrent fetches and excessive API calls
    if (fetchInProgress.current || refreshInProgress.current) return;
    fetchInProgress.current = true;
    
    setIsLoading(true);

    try {
      const response = await fetch('/api/user', {
        headers: {
          'Cache-Control': 'no-cache',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch user data: ${response.status} ${response.statusText}`);
      }

      const { user: userData } = await response.json();
      
      if (!userData) {
        setUser(null);
        updateStoredUser(null);
        return;
      }
      
      // Update local state
      setUser(userData);
      updateStoredUser(userData);
      lastFetchTimeRef.current = Date.now();
      setError(null);
    } catch (err) {
      const error = err as Error;
      setError(error);
      
      // Keep using the stored user data if available
      if (storedUser) {
        setUser(storedUser);
      }
    } finally {
      setIsLoading(false);
      fetchInProgress.current = false;
    }
  }, [shouldFetch, updateStoredUser, storedUser]);

  // Initialize from stored user data on mount
  useEffect(() => {
    if (storedUser && !user) {
      setUser(storedUser);
      setIsLoading(false);
    }
  }, [storedUser, user]);

  // Fetch user data when auth is loaded and user is signed in
  useEffect(() => {
    // Only fetch if not already done
    if (isLoaded && isSignedIn && !user && !fetchInProgress.current) {
      fetchUserData();
    } else if (isLoaded && !isSignedIn) {
      setUser(null);
      updateStoredUser(null);
      setIsLoading(false);
    }
  }, [isLoaded, isSignedIn, fetchUserData, updateStoredUser, user]);

  // Define refreshUser with debounce to prevent spam
  const refreshUser = useCallback(() => {
    if (refreshInProgress.current) return;
    
    refreshInProgress.current = true;
    lastFetchTimeRef.current = null; // Reset the cooldown
    
    // Clear cached user data to force fresh fetch from server
    updateStoredUser(null);
    
    // Fetch after a short delay and only if no other fetch is in progress
    setTimeout(() => {
      if (!fetchInProgress.current) {
        fetchUserData(true);
      }
      refreshInProgress.current = false;
    }, 100);
  }, [fetchUserData, updateStoredUser]);

  // Simple enableFetching function that just resets the cooldown
  const enableFetching = useCallback(() => {
    if (lastFetchTimeRef.current === null || Date.now() - lastFetchTimeRef.current > 10000) {
      lastFetchTimeRef.current = null;
      fetchUserData(true);
    }
  }, [fetchUserData]);

  return {
    user,
    isLoading: isLoading || !isLoaded,
    error,
    isAdmin: user?.role === UserRole.ADMINISTRADOR || user?.role === UserRole.TITULAR,
    isTitular: user?.role === UserRole.TITULAR,
    isRevendedor: user?.role === UserRole.REVENDEDOR,
    isCliente: user?.role === UserRole.CLIENTE,
    isEditor: user?.role === UserRole.EDITOR,
    enableFetching,
    refreshUser
  };
} 